import { createRouter, createWebHistory, createMemoryHistory } from 'vue-router';
import { nextTick } from 'vue';
import authConfig from '@sharedModules/config/staticAuth';
import { forEach, get } from 'lodash';
import loadRoutes from '@/utils/vue-plugins/loadRoutes';
import i18n from '@/plugins/vue-i18n';
import { useContextStore } from '@/stores/context';
import { isEmbedded } from '@/webshell-embed/embedded';
import { getTabTitleDetailFromRoute } from '@/utils/tab-title-from-route';

const loginRoutes = ['/login', '/ow-auth/login', '/auth-openid/login', '/auth-saml/login'];

const SHELL_OWNS_LOGIN = 'the shell owns login while embedded';

// Embedded, the shell owns login, so AO never routes to its own login pages.
function disableLoginPagesWhenEmbedded(to) {
  if (loginRoutes.includes(to.path)) throw new Error(SHELL_OWNS_LOGIN);
}

export const navigationGuards = {
  ensureAuthenticated: to => {
    const contextStore = useContextStore();
    // If not authenticated, navigate to the login page
    const { profile, expireAt } = contextStore;
    const isTokenExpired = expireAt < Date.now();

    if (profile && isTokenExpired) {
      // if auth token is expired a refresh should be requested
      contextStore
        .refreshUserContext()
        .catch(error => console.warn('[assortment] token refresh failed', error));
    }

    if (!profile && !loginRoutes.includes(to.path)) {
      if (isEmbedded()) throw new Error(SHELL_OWNS_LOGIN);
      console.log('No valid user session, client forced redirect to /login page.');
      const loginPage = get(
        authConfig.authenticationPaths,
        `${contextStore.currentLoginMethod}.login`,
        '/login'
      );
      return { path: loginPage, query: { returnPath: to.path } };
    }

    return true;
  },
  ensureAuthorized: to => {
    const contextStore = useContextStore();
    // Here we ensure a user has the required permissions to access the "to" page
    if (to.meta.requiresPermission && !contextStore.hasPermission(to.meta.requiresPermission)) {
      console.error('accessing page without required permissions');
      return { path: '/error/403' };
    }
    return true;
  },
  ensureEnabled: to => {
    const contextStore = useContextStore();
    // Here we ensure that the "to" page is enabled before allowing the user access
    const isEnabled = get(contextStore, to.meta.featureKey, true);
    if (!isEnabled) return { path: '/' };
    return true;
  },
  maintainCurrentTenantInQuery: to => {
    // In multitenant environments, maintain the current tenant as a query string parameter
    // so the URLs can be copy/pasted and shared by simply copying the URL in the browser.
    // Otherwise the URLs wont load correctly unless the user knows which tenant to select first.
    const contextStore = useContextStore();
    if (contextStore.isMultitenancyEnabled) {
      if (!to.query.tenant) {
        to.query.tenant = contextStore.getCurrentTenant;
        return { ...to, replace: true };
      }
    }
    return true;
  },
};

// Embedded, the shell owns the browser URL. AO is DOM-mounted into the shell's document, and two
// `createWebHistory` routers there share `history.state`, each rewriting the other's entries
// against its own base. On a memory history AO can't; it reports where it went instead.
export function createAppRouter(base = import.meta.env.BASE_URL, { embedded = false } = {}) {
  const routes = loadRoutes(import.meta.glob(['@/pages/**/*.router.js'], { eager: true }));
  const router = createRouter({
    // Embedded, the base only shapes hrefs, so links and new tabs open the shell URL.
    history: embedded ? createMemoryHistory(base) : createWebHistory(base),
    routes,
  });
  if (embedded) router.beforeEach(disableLoginPagesWhenEmbedded);
  forEach(navigationGuards, guard => router.beforeEach(guard));

  // Update title to make history show properly. Embedded, the shell owns the tab and
  // shell-contributions.js reports the same detail to it instead - ADR-018 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/018-shell-owns-the-tab-title.md
  router.afterEach(async to => {
    if (embedded) return;
    const appTitle = i18n.t('general.appTitle', { fallback: 'Assortment' });
    const title = [appTitle, getTabTitleDetailFromRoute(to)].filter(Boolean).join(' | ');

    await nextTick();
    document.title = title;
  });

  return router;
}
